const API_KEY = "sk_counter_api_2024_secure_key_12345"
const BASE_URL = "https://zcounter.zithera.com.au/api/v1/counter"

export interface CounterResponse {
  count: number
  counter_name: string
  status: string
}

export class CounterService {
  // Increment a specific counter
  static async incrementCounter(counterName: string): Promise<number> {
    try {
      const response = await fetch(`${BASE_URL}/${counterName}/increment?api_key=${API_KEY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to increment counter: ${response.status}`)
      }

      const data: CounterResponse = await response.json()
      return data.count
    } catch (error) {
      console.error(`Error incrementing counter ${counterName}:`, error)
      return 0
    }
  }

  // Get current count for a specific counter
  static async getCounter(counterName: string): Promise<number> {
    try {
      const response = await fetch(`${BASE_URL}/${counterName}?api_key=${API_KEY}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to get counter: ${response.status}`)
      }

      const data: CounterResponse = await response.json()
      return data.count
    } catch (error) {
      console.error(`Error getting counter ${counterName}:`, error)
      return 0
    }
  }

  // Increment category-specific counter and total counter
  static async trackQuoteGeneration(category?: string): Promise<void> {
    try {
      // Always increment total counter
      await this.incrementCounter("total-quotes")

      // Increment category-specific counter if category is provided
      if (category) {
        await this.incrementCounter(`${category}-quotes`)
      } else {
        // If no category, increment general quotes
        await this.incrementCounter("general-quotes")
      }
    } catch (error) {
      console.error("Error tracking quote generation:", error)
    }
  }

  // Get all counter stats
  static async getAllStats(): Promise<{
    total: number
    fitness: number
    business: number
    success: number
    golf: number
    eating: number
    calm: number
    humble: number
    study: number
    stress: number
    relationship: number
    general: number
  }> {
    try {
      const [total, fitness, business, success, golf, eating, calm, humble, study, stress, relationship, general] = await Promise.all([
        this.getCounter("total-quotes"),
        this.getCounter("fitness-quotes"),
        this.getCounter("business-quotes"),
        this.getCounter("success-quotes"),
        this.getCounter("golf-quotes"),
        this.getCounter("eating-quotes"),
        this.getCounter("calm-quotes"),
        this.getCounter("humble-quotes"),
        this.getCounter("study-quotes"),
        this.getCounter("stress-quotes"),
        this.getCounter("relationship-quotes"),
        this.getCounter("general-quotes"),
      ])

      return {
        total,
        fitness,
        business,
        success,
        golf,
        eating,
        calm,
        humble,
        study,
        stress,
        relationship,
        general,
      }
    } catch (error) {
      console.error("Error getting all stats:", error)
      return {
        total: 10000, // Fallback numbers
        fitness: 2500,
        business: 2000,
        success: 2200,
        golf: 1800,
        eating: 1700,
        calm: 1500,
        humble: 1200,
        study: 1600,
        stress: 1300,
        relationship: 1400,
        general: 1500,
      }
    }
  }
}
